import Serialization, {
  type DocData,
  type CopiedWhiteboardContent,
  type PastedWhiteboardContent,
} from '../collaboration/Serialization';
import Tool from './Tool';
import version from '../version';
import { randomId } from '@aha-app/mvc';
import { fabric } from 'fabric';
import { fireCreatedDeep } from '../utils/objects';
import type WhiteboardController from '../controllers/WhiteboardController';
import { getMaxZ } from '../utils/zManager';
import paper from 'paper';
import Shape from '../objects/Shape';
import type ShapeCustomPath from '../shapes/ShapeCustomPath';
import { type RecordId } from '../shared-types';
import { type IntegrationRecordReference } from '../controllers/IntegrationsController';
import { RECORD_DEFAULT_HEIGHT } from '../constants';
import Debug from 'debug';
import SVGImporter from '../utils/SVGImporter';
import { CustomShape } from '../models/CustomShape';

import { buildWhiteboardsJSONFromHTML } from './ToolPasteHTMLUtils';
import { sanitizeTextForEditor } from './ToolPasteTextUtils';
import { parseAssistantArtifactUrls } from './ToolPasteArtifactUtils';
import { hasFrameMemberBehaviors } from '../objects/guards';
import { type RoadmapImportFormat } from '../components/modals/RoadmapImportModalController';

const debug = Debug('whiteboard');

type PasteShapes = PastedWhiteboardContent<Partial<Shape>>;

const SVG_PATH_REGEX = /^[Mm]\s*\d+[MmZzLlHhVvCcSsQqTtAa\d-,.\s]+[Zz]?$/;
const SVG_REGEX = /^<svg[^>]*>.*<\/svg>$/s;
const URL_REGEX = /^https:\/\/[^/]+/;

const RECORD_REGEXES = [
  {
    recordType: 'features',
    regex: /\/features\/([A-Z][A-Z0-9]*-[0-9]+)/,
  },
  {
    recordType: 'requirements',
    regex: /\/requirements\/([A-Z][A-Z0-9]*-[0-9]+-[0-9]+)/,
  },
  {
    recordType: 'epics',
    regex: /\/epics\/([A-Z][A-Z0-9]*-E-[0-9]+)/,
  },
  {
    recordType: 'releases',
    regex: /\/releases\/([A-Z][A-Z0-9]*-R-[0-9]+)/,
  },
  {
    recordType: 'releases',
    regex: /\/master_releases\/([A-Z][A-Z0-9]*-R-[0-9]+)/,
  },
  {
    recordType: 'releasePhases',
    regex: /\/release_phases\/([0-9]+)/,
  },
  {
    recordType: 'ideas',
    regex: /\/ideas\/([A-Z][A-Z0-9]*-I-[0-9]+)/,
  },
  {
    recordType: 'ideaThemes',
    regex: /\/idea_themes\/([A-Z][A-Z0-9]*-T-[0-9]+)/,
  },
  {
    recordType: 'initiatives',
    regex: /\/initiatives\/([A-Z][A-Z0-9]*-S-[0-9]+)/,
  },
  {
    recordType: 'goals',
    regex: /\/strategic_imperatives\/([0-9]+|[A-Z][A-Z0-9]*-G-[0-9]+)/,
  },
  {
    recordType: 'pages',
    regex: /\/pages\/([A-Z][A-Z0-9]*-N-[0-9]+)/,
  },
  {
    recordType: 'tasks',
    regex: /\/tasks\/([0-9]+|[A-Z][A-Z0-9]*-G-[0-9]+)/,
  },
  {
    recordType: 'keyResults',
    regex: /\/key_results\/([A-Z][A-Z0-9]*-G-[0-9]+-KR-[0-9]+)/,
  },
  {
    recordType: 'projects',
    regex: /\/projects\/([A-Z][A-Z0-9]+)/,
  },
] as const;

// A list of roadmaps, how to match them by URL, and the available import
// formats. Low tech solution, but works for now. The ideal implementation would
// probably involved calling Rails to identif a report, since some reports
// don't have the id easily available in the URL.
const ROADMAP_REGEXES: {
  regex: RegExp;
  formats: RoadmapImportFormat[];
}[] = [
  {
    regex: /\/bookmarks\/(?:gantt_roadmaps)\/([0-9]+)(?:\/([0-9]+))?/,
    formats: ['records', 'image', 'embedded'],
  },
  {
    regex:
      /\/bookmarks\/(?:strategy_roadmaps|gantt_charts|epics|features|initiatives)\/([0-9]+)(?:\/([0-9]+))?/,
    formats: ['records', 'image', 'embedded'],
  },
  {
    regex:
      /\/bookmarks\/(?:custom_pivots|idea_priority_lists|idea_grids|feature_priority_lists|feature_grids|project_timelines|initiative_gantt_roadmaps)\/([0-9]+)(?:\/([0-9]+))?/,
    formats: ['records', 'image'],
  },
  {
    regex:
      /\/bookmarks\/(?:idea_priority_charts|roadmaps|now_next_later_roadmaps|portfolio_roadmaps|product_value_reports)\/([0-9]+)(?:\/([0-9]+))?/,
    formats: ['image'],
  },
] as const;

// We use semantic versioning (see README.md) and only verify the major
// version matches.
function acceptableVersion(clipVersion: string) {
  return version.split('.')[0] === clipVersion.split('.')[0];
}

function parseIntegrationsUrl(
  controller: WhiteboardController,
  rawText: string
): IntegrationRecordReference | undefined {
  const integrationServices = Object.keys(
    controller.integrationsController.integrations
  );

  // Check if the URL belongs to any of the existing integrations
  for (const integrationService of integrationServices) {
    const integration =
      controller.integrationsController.getIntegration(integrationService);
    const recordId = integration?.urlToRecordId(rawText);

    if (recordId) {
      if (!window.currentAccount?.whiteboardsAdvanced) {
        controller.actionShowMessage({
          type: 'integrationUpgradePrompt',
          integration,
        });
        return;
      }

      if (!controller.props.allowUpdate) {
        // TODO maybe tell the user they weren't allowed?
        return;
      }

      return {
        type: 'recordReference',
        recordType: integrationService,
        recordKey: recordId,
        recordUrl: rawText,
      };
    }
  }
}

export function parseUrl(controller: WhiteboardController, rawText: string) {
  const shapes: PasteShapes['objects'] = {};
  const urls = rawText.split(/\s*\n\s*/);

  // Match against Aha! record URLs
  urls.forEach(url => {
    for (const recordPattern of RECORD_REGEXES) {
      const matches = url.match(recordPattern.regex);
      if (matches) {
        const recordId = matches[1] as RecordId;
        const objectId = randomId() as RecordId;

        shapes[objectId] = {
          id: objectId,
          type: 'shape',
          shapeName: 'record',
          authorId: controller.state.userId,
          recordType: recordPattern.recordType,
          recordId,
          // Offset top to space them out
          top: Object.keys(shapes).length * (RECORD_DEFAULT_HEIGHT + 10),
          left: 0,
        };
      }
    }
  });

  if (Object.keys(shapes).length > 0) {
    return {
      objects: shapes,
      version,
    };
  }

  controller.actionShowMessage(
    'The pasted URL(s) is not a supported record type'
  );
}

/**
 * Creates a single text shape containing all the pasted text,
 * with each line as a separate paragraph.
 */
async function textToObjects(
  controller: WhiteboardController,
  rawText: string
): Promise<PasteShapes> {
  const rows = rawText
    .split(/\r|\n/)
    .map(r => r.trim())
    .filter(r => r.length > 0);

  const doc: PasteShapes = {
    objects: {},
    version,
  };

  // Create a single text box with all paragraphs
  const objectId = randomId();
  const textContent = rows
    .map(row => `<p>${sanitizeTextForEditor(row)}</p>`)
    .join('<p><br></p>');

  const textShapeAttributes = {
    id: objectId,
    type: 'shape',
    shapeName: 'text',
    textContent,
    left: 0,
    top: 0,
    ...controller.defaultsManager.for('shape', 'text'),
    width: 400,
    wrapText: true,
  };
  doc.objects[objectId] = textShapeAttributes;

  const measureShape = new Shape({
    controller,
    ...textShapeAttributes,
  });
  const dimensions = await measureShape.measureTextDimensions();
  textShapeAttributes.height = Math.max(dimensions.height, 20);

  return doc;
}

function createCustomPathShape({
  controller,
  pathData,
}: {
  controller: WhiteboardController;
  pathData: string;
}) {
  const path = new paper.CompoundPath(pathData);
  const position = controller.mousePosition;
  path.position = new paper.Point(position.x, position.y);

  const left = path.bounds.topLeft.x;
  const top = path.bounds.topLeft.y;
  const width = path.bounds.width;
  const height = path.bounds.height;

  path.position = new paper.Point(0, 0);

  // Don't create a shape if the path is empty.
  if (!path.pathData) {
    return;
  }

  // Check the path array that fabric uses.
  // If it's not valid, don't create a shape.
  const fabricPath = fabric.util.makePathSimpler(
    fabric.util.parsePath(path.pathData)
  );

  if (!Array.isArray(fabricPath) || fabricPath.length === 0) {
    return;
  }

  for (const command of fabricPath) {
    if (
      typeof command[0] !== 'string' ||
      !command.slice(1).every(n => n === Number(n))
    ) {
      return;
    }
  }

  const customPathShape = Shape.create<ShapeCustomPath>({
    controller,
    ...controller.defaultsManager.for('shape'),
    shapeName: 'customPath',
    left,
    top,
    width,
    height,
    angle: 0,
    scaleX: 1,
    scaleY: 1,
    shapePath: path.pathData,
  });

  controller.canvas.replaceSelectionWith(customPathShape);

  controller.actionShowObjectToolbar();
}

class ToolPaste extends Tool {
  static toolName = 'Paste objects';
  // Note: This tool doesn't have a shortcut registered, as it's manually
  // triggered by listening to `paste` events in the whiteboard controller.

  static displayedPasteWarning = false;

  static async pasteSVGTextAsShapes(
    controller: WhiteboardController,
    rawText: string
  ) {
    const previousShapeAnalyticsSetting = controller.sendShapeCreationAnalytics;
    try {
      controller.sendShapeCreationAnalytics = false;

      const importer = new SVGImporter(controller);
      const doc = await importer.import(rawText);
      const clipboardContents = {
        ...doc,
        version,
      };
      await this.pasteContents(controller, clipboardContents);
    } finally {
      controller.sendShapeCreationAnalytics = previousShapeAnalyticsSetting;
    }
  }

  static async readText(controller: WhiteboardController, text: string) {
    const contents = await textToObjects(controller, text);

    if (!contents) {
      return false;
    }

    await this.pasteContents(controller, contents);

    return true;
  }

  // Moves all the objects in docData to as if they were selected and dragged to
  // the new position.
  //
  // In other workds, find the top left of the bounding box around the objects
  // in the doc data, then move each object in docData be the same distance from
  // the new position as it was from the top left of the bounding box.
  private static moveObjectsToPosition(
    docData: DocData,
    newPosition: fabric.IPoint
  ) {
    let newDocTop = Infinity;
    let newDocLeft = Infinity;

    Object.values(docData.objects).forEach(obj => {
      newDocTop = Math.min(newDocTop, obj.top ?? Infinity);
      newDocLeft = Math.min(newDocLeft, obj.left ?? Infinity);

      for (const point of obj.points ?? []) {
        newDocTop = Math.min(newDocTop, point.y ?? Infinity);
        newDocLeft = Math.min(newDocLeft, point.x ?? Infinity);
      }
    });

    const leftOffset = newPosition.x - newDocLeft;
    const topOffset = newPosition.y - newDocTop;

    Object.values(docData.objects).forEach(obj => {
      if (obj.left !== undefined && obj.top !== undefined) {
        obj.left += leftOffset;
        obj.top += topOffset;
      }
      for (const point of obj.points ?? []) {
        if (point.x !== undefined && point.y !== undefined) {
          point.x += leftOffset;
          point.y += topOffset;
        }
      }
    });
  }

  private static handleCustomShapes(
    controller: WhiteboardController,
    contents: PastedWhiteboardContent
  ) {
    if (!contents.customShapes) {
      return;
    }

    if (!controller.props.allowUpdate) {
      controller.actionShowMessage(
        'You do not have permission to add custom shapes to this whiteboard.'
      );
    } else if (contents.whiteboardId === controller.whiteboardId) {
      controller.actionShowMessage(
        "Can't add new custom shapes into the whiteboard they come from."
      );
    } else {
      Object.values(contents.customShapes).forEach(data => {
        const customShape = new CustomShape({
          name: data.name,
          objects: data.objects,
        });
        controller.dataStoreCustomShapes.addRecord(customShape);
      });

      controller.actionShowMessage(
        `${
          Object.keys(contents.customShapes).length
        } custom shapes created. You can find them in the shapes menu.`
      );
      // Open the shapes menu if it's not already open. If it's pinned open we don't
      // do anything. I tried to get it to unpin, close, and reopen but I could only
      // get it to unpin. Setting another stylus as active didn't close the menu even
      // after it was unpinned.
      // I don't think it's worth chasing what should be a prety rare case.
      controller.actionSetStylusMenu(controller.currentShapeStylus);
    }
  }

  static async pasteContents(
    controller: WhiteboardController,
    contents: PastedWhiteboardContent,
    position: fabric.IPoint = controller.mousePosition
  ) {
    this.handleCustomShapes(controller, contents);

    // Reviewers can only paste their own comments.
    if (!controller.props.allowUpdate) {
      for (const obj of Object.values(contents.objects) as fabric.Object[]) {
        if (
          obj.type !== 'comment' ||
          obj.authorId !== controller.state.userId
        ) {
          delete contents.objects[obj.id];
        }
      }
    }

    if (!Object.keys(contents.objects).length) {
      return;
    }

    // Create a copy of the clipboard with remapped IDs.
    const newDoc = await Serialization.cloneDocumentWithAttachments(
      contents,
      controller
    );

    controller.presentationController.prepareFramesForPaste(newDoc);

    // Move all the objects to their new position by manually updating the
    // coordinates in the doc. We do this before it is ever inserted on the
    // canvas to avoid any unwanted side effects of placing the content in
    // the wrong place before moving it.
    this.moveObjectsToPosition(newDoc, position);

    // Restore the document back onto the canvas.
    const selectedObjects = Serialization.restoreDocument(controller, newDoc);

    // Select the pasted objects.
    const activeSelection =
      controller.canvas.makeActiveSelection(selectedObjects);

    // Make sure the pasted objects are in the front.
    activeSelection.bringToFront();

    // Fire created events on the pasted objects. This allows hidden comments
    // to be shown in the side panel.
    selectedObjects.forEach(o => {
      fireCreatedDeep(o);
    });

    // capture new objects that are not already within a frame within any existing frames on the canvas
    for (const obj of selectedObjects
      .filter(hasFrameMemberBehaviors)
      .filter(obj => !obj.container)) {
      obj.maybeSetParentFrame(selectedObjects);
    }

    // Update the toolbar to appear in the right place.
    controller.actionShowObjectToolbar();

    this.toolActionComplete({ controller });
  }

  private static async readImage({
    controller,
    blob,
    type,
  }: {
    controller: WhiteboardController;
    blob: Blob;
    type: string;
  }) {
    const file = new File([blob], 'Pasted image', { type });
    await controller.actionUploadImage(file, controller.mousePosition);
  }

  private static async readHTML(controller: WhiteboardController, blob: Blob) {
    const htmlText = await blob?.text();

    if (!htmlText) {
      return false;
    }

    const whiteboardObjects = buildWhiteboardsJSONFromHTML(
      controller,
      htmlText,
      controller.mousePosition.x,
      controller.mousePosition.y,
      getMaxZ(controller.canvas.getObjects()) + 1
    );

    if (Object.keys(whiteboardObjects).length === 0) {
      console.debug('No compatible content found in pasted HTML');
      return false;
    }

    // Add the table to the whiteboard
    Serialization.restoreDocument(controller, { objects: whiteboardObjects });

    return true;
  }

  private static async pasteFromLastCopiedContents(
    controller: WhiteboardController
  ) {
    // It wasn't possible to read from the clipboard, fall back to using the
    // last thing copied in this whiteboard instance.
    const contents = Serialization.transformWhiteboardContent(
      controller.clipboard,
      controller
    );

    if (contents) {
      await this.pasteContents(controller, contents);
    }
  }

  private static async handleSvg(
    controller: WhiteboardController,
    rawText: string
  ): Promise<boolean> {
    if (rawText.trim().match(SVG_PATH_REGEX)) {
      createCustomPathShape({ controller, pathData: rawText.trim() });
      return true;
    } else if (rawText.trim().match(SVG_REGEX)) {
      await this.pasteSVGTextAsShapes(controller, rawText);
      return true;
    }

    return false;
  }

  static async pasteUrl(
    controller: WhiteboardController,
    rawText: string
  ): Promise<boolean> {
    if (rawText.match(URL_REGEX)) {
      if (!controller.allowRecordLinking()) {
        controller.actionShowMessage(
          'URLs cannot be pasted on a shared whiteboard'
        );
        return false;
      }

      let parsedUrl: URL | undefined;

      // Parse the URL to get the pathname
      try {
        parsedUrl = new URL(rawText);
      } catch {
        // Ignore error
      }

      if (parsedUrl) {
        const pathname = parsedUrl.pathname;

        for (const roadmapTypeInfo of ROADMAP_REGEXES) {
          const pattern = roadmapTypeInfo.regex;

          const matches = pathname.match(pattern);

          if (matches) {
            // If we have a second ID (working copy ID), use that, otherwise use the first ID
            const bookmarkId = (matches[2] || matches[1]) as RecordId;
            controller.actionSetShowRoadmapImportModal({
              bookmarkId,
              availableFormats: roadmapTypeInfo.formats,
            });
            return true;
          }
        }
      }

      const integrationUrlData = parseIntegrationsUrl(controller, rawText);

      if (integrationUrlData) {
        controller.integrationsController.actionAddRecord(
          integrationUrlData,
          controller.mousePosition
        );

        return true;
      }

      const assistantArtifacts = parseAssistantArtifactUrls(rawText);
      if (assistantArtifacts.length > 0) {
        console.debug('found artifact', assistantArtifacts);
        assistantArtifacts.forEach(
          async artifact =>
            await controller.actionPlaceAssistantArtifact(artifact)
        );
        return true;
      }

      const urlContents = parseUrl(controller, rawText);

      if (urlContents) {
        await this.pasteContents(controller, urlContents);
        return true;
      }
    }

    return false;
  }

  /**
   * If the given text is a valid whiteboard JSON string then
   * parse it and add the content to the whiteboard.
   */
  private static async handleWhiteboardJSON(
    controller: WhiteboardController,
    rawText: string
  ): Promise<boolean> {
    let whiteboardJSON: CopiedWhiteboardContent;

    try {
      whiteboardJSON = JSON.parse(rawText) as CopiedWhiteboardContent;
    } catch (e) {
      debug('Failed to parse whiteboard JSON:', e);
      return false;
    }

    if (!whiteboardJSON.objects || !acceptableVersion(whiteboardJSON.version)) {
      return false;
    }

    // Convert orphaned MindMapLeaf objects to Shape objects during paste
    if (whiteboardJSON.objects) {
      Object.values(whiteboardJSON.objects).forEach(obj => {
        if (obj.type === 'mindMapLeaf') {
          // Only convert if the leaf's container (MindMapBranch) is not in the clipboard
          if (!obj.container || !whiteboardJSON.objects[obj.container]) {
            obj.type = 'shape';
          }
        }
      });
    }

    const contents = Serialization.transformWhiteboardContent(
      whiteboardJSON as PastedWhiteboardContent,
      controller
    );

    if (!contents) {
      return false;
    }

    await this.pasteContents(controller, contents);

    return true;
  }

  /**
   * Reads the various formats from the clipboard until it finds one we can
   * handle.
   */
  private static async readFormattedText(
    controller: WhiteboardController
  ): Promise<boolean> {
    let copySuccess = false;
    let clipboardItems: ClipboardItem[];

    try {
      clipboardItems = await navigator.clipboard.read();
    } catch (e) {
      console.error('Failed to read formatted clipboard contents:', e);
      return false;
    }

    // Order formats: image first, then html, then plain
    const formatOrder = [
      (type: string) => type.startsWith('image/'),
      (type: string) => type === 'text/html',
      (type: string) => type === 'text/plain',
    ];

    for (const item of clipboardItems) {
      // We can handle multiple items, but for now, stop after processing one
      if (copySuccess) break;

      for (const checkFormat of formatOrder) {
        if (copySuccess) break;

        const type = item.types.find(checkFormat);

        if (type) {
          if (type.startsWith('image/')) {
            const blob = await item.getType(type);
            await this.readImage({ controller, blob, type });
            copySuccess = true;
          } else if (type === 'text/html') {
            const blob = await item.getType(type);
            copySuccess = await this.readHTML(controller, blob);
          } else if (type === 'text/plain') {
            const blob = await item.getType(type);
            const text = await blob.text();
            await this.readText(controller, text);
            copySuccess = true;
          }
        }
      }
    }

    return copySuccess;
  }

  static async handleRawPastedText(
    controller: WhiteboardController,
    rawText: string
  ) {
    let copySuccess = false;
    copySuccess ||= await this.handleSvg(controller, rawText);
    copySuccess ||= await this.pasteUrl(controller, rawText);
    copySuccess ||= await this.handleWhiteboardJSON(controller, rawText);
    copySuccess ||= await this.readFormattedText(controller);
    copySuccess || (await this.readText(controller, rawText));
  }

  static async action({ controller }: { controller: WhiteboardController }) {
    // Switch to selection mode if currently in pan mode
    if (controller.state.currentStylus.isCurrentStylus(controller.stylusPan)) {
      controller.actionSetStylus(controller.stylusPointer);
    }

    let rawText: string;

    // Get the clipboard raw text value. If we can't access it, fall back to
    // using what was stored in the controller. This allows copy/paste to work
    // on a single whiteboard even if the clipboard is not working.
    if (!navigator.clipboard?.readText) {
      controller.actionShowPasteUnsupportedMessage();
      await this.pasteFromLastCopiedContents(controller);
      this.toolActionComplete({ controller });
      return;
    } else {
      try {
        rawText = await navigator.clipboard.readText();
      } catch (e) {
        debug('Failed to read clipboard text:', e);
        await this.pasteFromLastCopiedContents(controller);
        this.toolActionComplete({ controller });
        return;
      }
    }

    this.handleRawPastedText(controller, rawText);
    this.toolActionComplete({ controller });
  }

  static enabled() {
    return true;
  }
}

export default ToolPaste;
